/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@ember/component';
import { observes } from '@ember-decorators/object';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty, isNone } from '@ember/utils';
import { bind, next } from '@ember/runloop';
import { task, timeout } from 'ember-concurrency';
import { buildWaiter } from '@ember/test-waiters';
import { tagName } from '@ember-decorators/component';
import { guidFor } from '@ember/object/internals';
import ace from 'ace/ace';
import {
  EMAIL_COMPOSER_ROLLBACK,
  INSERT_LIQUID,
  LIQUID_ENABLED_FIELD_FOCUS_IN,
  LIQUID_ENABLED_FIELD_FOCUS_OUT,
  FOCUS_ACE_EDITOR,
} from 'cio/services/event-bus';
import config from 'cio/config/environment';
import 'cio/utils/ace/theme-cio-dark';
import { getLiquidToInsert } from 'cio/utils/liquid';

let onChangeWaiter = buildWaiter('cio-ace-editor:on-change-waiter');
let validationWaiter = buildWaiter('cio-ace-editor:validation-waiter');

const DEBOUNCE_DURATION = config.APP.debounceDuration;

const DEFAULT_MARGINS = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

@tagName('')
export default class CioAceEditor extends Component {
  @service eventBus;

  // external API
  content = '';
  minLines = undefined;
  maxLines = undefined;
  template = undefined;
  disableEditing = false;

  isDirty = false;

  @computed('editorId')
  get editorIdWithDefault() {
    return this.editorId || `${guidFor(this)}_ace-editor`;
  }

  mode = 'html';
  validate = false;
  isLiquidInsertionDisabled = false;
  focusOnInsert = false;
  readOnly = false;
  highlightActiveLine = true;
  highlightSelectedWord = true;
  startOnLine = 0;
  enableAutocomplete = false;
  editorSession = undefined;
  margins = undefined;
  lineHeight = undefined;
  onContentChange() {}
  onValidate() {}
  onInitialize() {}

  parentElement = null;
  resizeObserver = null;

  @observes('isDirty')
  isDirtyDidChange() {
    this.triggerResize();
  }

  @observes('readOnly', 'disableEditing')
  readOnlyStatusDidChange() {
    let { editorIdWithDefault } = this;
    let editor = window.aceEditorInstances[editorIdWithDefault];

    editor.setReadOnly(!!(this.readOnly || this.disableEditing));
  }

  @computed('margins')
  get _margins() {
    return { ...DEFAULT_MARGINS, ...(this.margins ?? {}) };
  }

  validationWaiterToken = null;

  _setValidation() {
    let { validate } = this;

    let editor = window.aceEditorInstances[this.editorIdWithDefault];
    let editorSession = editor.getSession();
    editorSession.setUseWorker(validate);

    // ace workers for different modes might need different setup.
    // Right now we only use ace's validation for JSON input, so this is enough.
    // If you need to add validation for different modes, look up the mode
    // in ace editor's source code and check the event listeners.
    if (validate && (this.mode === 'json' || this.mode === 'javascript')) {
      editor.on(
        'changeMode',
        bind(this, () => {
          let worker = editorSession.$worker;

          // overwrite ace editor's default worker event listener
          // with a custom one. This is so we can easily debounce
          // validation.
          worker.removeAllListeners('annotate');
          worker.on(
            'annotate',
            bind(this, (event) => {
              this._debouncedSetAnnotations.perform(event.data);
            }),
          );
        }),
      );

      // because validation happens in an external web worker,
      // we need to register this test waiter,
      // so that tests know when async actions are finished.
      // This is the same as below in _aceContentDidChange, but we need to run
      // this once at the start as well, so that tests wait for validations on
      // initial editor content too (_aceContentDidChange only runs on changes,
      // not on the initial render)
      if (!isEmpty(this.content)) {
        this.validationWaiterToken = validationWaiter.beginAsync();
      }
    }
  }

  _debouncedSetAnnotations = task(
    {
      restartable: true,
    },
    async (data) => {
      await timeout(DEBOUNCE_DURATION);
      window.aceEditorInstances[this.editorIdWithDefault].getSession().setAnnotations(data);
      this.onValidate(isEmpty(data));

      if (this.validationWaiterToken != null) {
        validationWaiter.endAsync(this.validationWaiterToken);
        this.validationWaiterToken = null;
      }
    },
  );

  _aceContentDidChange() {
    let token = onChangeWaiter.beginAsync();

    let content = window.aceEditorInstances[this.editorIdWithDefault].getValue();
    this.set('content', content);
    this.onContentChange?.(content);

    // because validation happens in an external web worker,
    // we need to register this test waiter,
    // so that tests know when async actions are finished
    if (this.validate && (this.mode === 'json' || this.mode === 'javascript') && this.validationWaiterToken == null) {
      this.validationWaiterToken = validationWaiter.beginAsync();
    }

    onChangeWaiter.endAsync(token);
  }

  @action
  _insertLiquid([fieldName, _fieldIndex], value, fallback) {
    let session = window.aceEditorInstances[this.editorIdWithDefault].getSession();

    if (fieldName === this.editorIdWithDefault) {
      let selection = session.selection.getRange();

      let insertValue = getLiquidToInsert(value, fallback, this.template?.templateEngine);

      if (this.mode === 'javascript') {
        insertValue = fallback ? `${value} ? ${value} : "${fallback}"` : value;
      }

      session.insert(selection.start, insertValue);
    }
  }

  _onEmailComposerRollback() {
    let { content } = this;
    let editor = window.aceEditorInstances[this.editorIdWithDefault];

    editor.removeAllListeners('change');
    editor.getSession().setValue(content);
    editor.on('change', bind(this, this._aceContentDidChange));
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);
    let { eventBus, isLiquidInsertionDisabled } = this;

    if (!isLiquidInsertionDisabled) {
      eventBus.unsubscribe(INSERT_LIQUID, this, this._insertLiquid);
    }

    eventBus.unsubscribe(FOCUS_ACE_EDITOR, this, this.focusEditor);
    eventBus.unsubscribe(EMAIL_COMPOSER_ROLLBACK, this, this._onEmailComposerRollback);
    window.aceEditorInstances[this.editorIdWithDefault].destroy();
    delete window.aceEditorInstances[this.editorIdWithDefault];
  }

  @action
  resetEditor() {
    this.editorSession.setValue(this.content);
  }

  @action
  focusEditor(name) {
    if (name !== this.editorIdWithDefault) {
      return;
    }

    this.editor.focus();
  }

  @action
  triggerResize() {
    let { editorIdWithDefault } = this;
    let editor = window.aceEditorInstances[editorIdWithDefault];

    if (typeof editor?.resize === 'function') {
      editor.resize();
    }
  }

  @action
  initializeEditor(element) {
    if (isNone(window.aceEditorInstances)) {
      window.aceEditorInstances = {};
    }

    let { eventBus } = this;
    let { editorIdWithDefault } = this;

    let editor = (window.aceEditorInstances[editorIdWithDefault] = ace.edit(editorIdWithDefault));

    let margins = this._margins;
    editor.renderer.setScrollMargin(margins.top, margins.bottom, margins.left, margins.right);

    if (this.lineHeight != null) {
      editor.container.style.lineHeight = this.lineHeight;
    }

    this.set('editor', editor);
    this.onInitialize(editorIdWithDefault);

    editor.textInput.getElement().tabIndex = 1;

    editor.$blockScrolling = Infinity;
    editor.setTheme('ace/theme/cio-dark');

    let editorSession = editor.getSession();
    this.set('editorSession', editorSession);
    editorSession.setUseWrapMode(true);

    this._setValidation();

    editorSession.setMode(`ace/mode/${this.mode}`);
    editorSession.setValue(this.content || '');
    editor.on('change', bind(this, this._aceContentDidChange));

    if (!this.isLiquidInsertionDisabled) {
      editor.on(
        'focus',
        bind(this, () => {
          this.eventBus.publish(LIQUID_ENABLED_FIELD_FOCUS_IN, [editorIdWithDefault, null]);
        }),
      );

      editor.on(
        'blur',
        bind(this, () => {
          this.eventBus.publish(LIQUID_ENABLED_FIELD_FOCUS_OUT);
        }),
      );

      eventBus.subscribe(INSERT_LIQUID, this, this._insertLiquid);
    }

    eventBus.subscribe(FOCUS_ACE_EDITOR, this, this.focusEditor);
    eventBus.subscribe(EMAIL_COMPOSER_ROLLBACK, this, this._onEmailComposerRollback);

    if (this.readOnly || this.disableEditing) {
      editor.setReadOnly(true);
    }

    editor.setOptions({
      minLines: this.minLines,
      maxLines: this.maxLines,
      highlightActiveLine: this.highlightActiveLine,
      highlightSelectedWord: this.highlightSelectedWord,
      enableBasicAutocompletion: this.enableAutocomplete,
      enableLiveAutocompletion: this.enableAutocomplete,
    });

    editor.container.style.lineHeight = 1.4;
    editor.renderer.updateFontSize();
    editor.resize();

    if (this.focusOnInsert) {
      editor.focus();
    }

    if (this.startOnLine > 0) {
      next(this, () => {
        editor.gotoLine(this.startOnLine);
        editor.focus();
      });
    }

    // We add the ResizeObserver to the parent element rather than the editor element itself because
    // the editor doesn't actually get resized vertically - it's always a zero height div. Ace editor
    // is absolutely positioned and it fills the full height of a parent.
    if (element.parentElement != null) {
      this.parentElement = element.parentElement;
      this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.triggerResize));
      this.resizeObserver.observe(this.parentElement);
    }
  }
}
